import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, catchError, map, throwError } from 'rxjs';
import { IAppConfig } from '../shared/interfaces/app-config';
import { AppConfigService } from './app-config.service';
import { IReferenceData } from '../shared/interfaces/form/reference-data.model';
import { Location } from '../shared/models/location';
import { handleError } from '../shared/functions/error-handler';
import { LcgmsLocationDetails } from '../shared/interfaces/lcgms-location';
import { IAssessmentPlanning } from '../modules/evaluations/interfaces/assessment-planning';
import { assertTrustedUrl } from '../shared/functions/validate-url';

@Injectable({
  providedIn: 'root',
})
export class ReferenceService {
  config: IAppConfig;

  isEditingStudentInfo$: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(false);

  constructor(
    private http: HttpClient,
    private appConfigService: AppConfigService
  ) {
    this.config = this.appConfigService.getConfig();
  }

  getDropDownValues(ddName: string): Observable<IReferenceData[]> {
    const url = assertTrustedUrl(
      `${this.config.apiUrl}/ReferenceValue/${encodeURIComponent(ddName)}`,
      this.config.apiUrl
    );
    return this.http
      .get<IReferenceData[]>(url)
      .pipe(
        catchError(this.handleError),
        map((data: IReferenceData[]) => (data))
      );
  }

  getAssessmentDropDownValues(
    type: string,
    id: string
  ): Observable<IAssessmentPlanning> {
    const url = assertTrustedUrl(
      `${this.config.apiUrl}/${encodeURIComponent(type)}/GetAssessmentPlanningByReferral/${encodeURIComponent(id)}`,
      this.config.apiUrl
    );
    return this.http
      .get<IAssessmentPlanning>(url)
      .pipe(catchError(handleError));
  }

  getLocations(): Observable<Location[]> {
    return this.http
      .get<Location[]>(`${this.config.apiUrl}/ReferenceValue/Locations`)
      .pipe(catchError(this.handleError));
  }

  getLcgmsLocations(): Observable<Location[]> {
    return this.http
      .get<Location[]>(`${this.config.apiUrl}/User/Locations`)
      .pipe(catchError(this.handleError));
  }

  getDohmhAgencies(): Observable<Location[]> {
    return this.http.get<unknown[]>(`${this.config.apiUrl}/User/Agencies`).pipe(
      map((data: unknown[]) => data?.map((d: unknown) => new Location(d))),
      catchError(this.handleError)
    );
  }

  getDohmhAgencyData(agencyId: string): Observable<LcgmsLocationDetails> {
    const url = assertTrustedUrl(
      `${this.config.apiUrl}/User/GetAgency/${encodeURIComponent(agencyId)}`,
      this.config.apiUrl
    );
    return this.http
      .get<LcgmsLocationDetails>(url)
      .pipe(catchError(this.handleError));
  }

  getAttendeesList(referralSK: string, caseStage: string): Observable<IReferenceData[]> {
    const url = assertTrustedUrl(
      `${this.config.apiUrl}/IEPSummary/GetAttendeesList/${encodeURIComponent(referralSK)}/${encodeURIComponent(caseStage)}`,
      this.config.apiUrl
    );
    return this.http
      .get<IReferenceData[]>(url)
      .pipe(catchError(this.handleError));
  }

  getAuthRoles(locationCode: string): Observable<IReferenceData[]> {
    const url = assertTrustedUrl(
      `${this.config.apiUrl}/User/GetAuthRoles/${encodeURIComponent(locationCode)}`,
      this.config.apiUrl
    );
    return this.http
      .get<IReferenceData[]>(url)
      .pipe(catchError(this.handleError));
  }

  // Error handling
  handleError(error: HttpErrorResponse): Observable<never> {
    let errorMessage: string = '';
    if (error.error instanceof ErrorEvent) {
      // Get client-side error
      errorMessage = error.error.message;
    } else {
      // Get server-side error
      errorMessage = `Error Code: ${error.status}\nMessage: ${error.message}`;
    }
    return throwError(() => {
      return errorMessage;
    });
  }
}
